import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { blogService } from '../../services/blogService';
import { LoadingSpinner } from '../../components/ui';
import type { BlogPost } from '../../types/blog';

export function BlogListPage() {
  const { user, isAdmin: userIsAdmin } = useAuth();
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState<BlogPost | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [publishingId, setPublishingId] = useState<string | null>(null);

  useEffect(() => {
    const loadBlogs = async () => {
      if (!userIsAdmin) return;

      try {
        setLoading(true);
        const allBlogs = await blogService.getAllBlogs();
        setBlogs(allBlogs);
      } catch (error) {
        console.error('Error loading blogs:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, [userIsAdmin]);

  const handleDeleteClick = (blog: BlogPost, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setBlogToDelete(blog);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!blogToDelete) return;

    try {
      setDeleting(true);
      await blogService.deleteBlogPost(blogToDelete.id);
      setBlogs((prev) => prev.filter((b) => b.id !== blogToDelete.id));
      setDeleteModalOpen(false);
      setBlogToDelete(null);
    } catch (error) {
      console.error('Error deleting blog:', error);
      alert('Failed to delete blog post. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setBlogToDelete(null);
  };

  const handlePublish = async (blog: BlogPost, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      setPublishingId(blog.id);
      await blogService.updateBlogPost(blog.id, { status: 'published' });
      setBlogs((prev) =>
        prev.map((b) => (b.id === blog.id ? { ...b, status: 'published' as const } : b))
      );
    } catch (error) {
      console.error('Error publishing blog:', error);
      alert('Failed to publish blog post. Please try again.');
    } finally {
      setPublishingId(null);
    }
  };

  if (!userIsAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to manage blog posts.</p>
        </div>
      </div>
    );
  }

  const filteredBlogs = blogs.filter((blog) => {
    if (filter === 'all') return true;
    return blog.status === filter;
  });

  return (
    <div className="px-4 sm:px-0 pb-12">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Manage Blog Posts</h1>
          <p className="text-gray-600 mt-2">Create and manage blog posts about local events</p>
        </div>
        <Link
          to="/admin/blog/create"
          className="px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium"
        >
          Create New Post
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Filter:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'all' | 'published' | 'draft')}
            className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Posts</option>
            <option value="published">Published</option>
            <option value="draft">Drafts</option>
          </select>
          <span className="text-sm text-gray-500">
            {filteredBlogs.length} post{filteredBlogs.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Blog List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : filteredBlogs.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <p className="text-gray-600 mb-4">No blog posts found.</p>
          <Link to="/admin/blog/create" className="text-primary-600 hover:text-primary-700 font-medium">
            Create your first blog post
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBlogs.map((blog) => (
            <div key={blog.id} className="relative group bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow">
              <Link to={blog.status === 'draft' ? `/admin/blog/edit/${blog.id}` : `/blog/${blog.id}`} className="block">
                {/* Featured Image */}
                {blog.featuredImage ? (
                  <div className="aspect-video bg-gray-200 overflow-hidden">
                    <img
                      src={blog.featuredImage}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                    <svg className="w-16 h-16 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                      />
                    </svg>
                  </div>
                )}

                <div className="p-4">
                {/* Status Badge */}
                <div className="mb-2">
                  <span
                    className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                      blog.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {blog.status === 'published' ? 'Published' : 'Draft'}
                  </span>
                </div>

                {/* Title */}
                <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                  {blog.title}
                </h3>

                {/* Excerpt */}
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{blog.excerpt}</p>

                {/* Metadata */}
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-2">
                    {blog.author.avatar && <img src={blog.author.avatar} alt="" className="w-5 h-5 rounded-full" referrerPolicy="no-referrer" />}
                    <span>{blog.author.name}</span>
                  </div>
                  <span>{blog.viewCount} views</span>
                </div>

                {/* Tags */}
                {blog.tags.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {blog.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="px-2 py-0.5 bg-primary-50 text-primary-700 rounded text-xs">
                        {tag}
                      </span>
                    ))}
                    {blog.tags.length > 3 && <span className="text-xs text-gray-400">+{blog.tags.length - 3}</span>}
                  </div>
                )}

                {/* Date */}
                <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                  {blog.status === 'published' && blog.publishedAt ? (
                    <>Published {blog.publishedAt.toDate().toLocaleDateString()}</>
                  ) : (
                    <>Created {blog.createdAt.toDate().toLocaleDateString()}</>
                  )}
                </div>
              </div>
              </Link>

              {/* Action Buttons */}
              <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 z-10">
                {/* Edit Button */}
                <Link
                  to={`/admin/blog/edit/${blog.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="p-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                  title="Edit blog post"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </Link>
                {/* Publish Button - Only for drafts */}
                {blog.status === 'draft' && (
                  <button
                    onClick={(e) => handlePublish(blog, e)}
                    disabled={publishingId === blog.id}
                    className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Publish blog post"
                  >
                    {publishingId === blog.id ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                )}
                {/* Delete Button */}
                <button
                  onClick={(e) => handleDeleteClick(blog, e)}
                  className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  title="Delete blog post"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">Delete Blog Post</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Are you sure you want to delete "{blogToDelete?.title}"?
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-6">
              This action cannot be undone. The blog post and all its data will be permanently removed.
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleCancelDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
